import React from 'react'
import Skeleton from 'react-loading-skeleton'

import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import FormattedPrice from 'src/shared/components/common/price/FormattedPrice'
import { t } from 'src/translations'

import {
  splitFreeDeliveryCredit,
  useAppliedFreeDelivery,
  useDiscountsRowFundedOffersTotal
} from 'public/components/default_template/offers/useFreeDeliveryOffer'
import {
  calculateDiscountsTotal,
  getEcommShippingFee,
  useCartLineItems,
  calculateSubtotalExcludingShippingFromCart
} from 'public/components/default_template/online_ordering/cart/cartUtils'
import DiscountedPrice from 'public/components/default_template/online_ordering/order/DiscountedPrice'
import ItemPrice from 'public/components/default_template/online_ordering/order/ItemPrice'
import { CashRoundingDisclaimer } from 'public/components/default_template/cash_rounding/CashRoundingDisclaimer'
import { CreditCardSurchargeDisclaimerWithItemPrice } from 'public/components/default_template/surcharges/CreditCardSurchargeDisclaimer'
import { useCart } from 'public/components/online_ordering/CartContext'
import { useCheckout } from 'public/components/online_ordering/CheckoutContext'
import { use3pdConfig } from 'public/components/online_ordering/FulfillmentContext'
import { usePayment } from 'public/components/online_ordering/PaymentContext'
import { useSpi } from './useSpi'
import { Channel } from 'public/js/siteUtilities'
import { FeeLineItems } from '../../order/FeeWithTooltip'

type OrderPricesProps = {
  canShowPaymentAdjustments?: boolean
}

/** Renders the line items impacting the order total */
const OrderPrices = ({ canShowPaymentAdjustments }: OrderPricesProps) => {
  const { uses3pd } = use3pdConfig()
  const { cart, loadingCart, subtotal, surcharges, channel } = useCart()
  const { giftCardAppliedAmount, toastCashAppliedAmount, orderTotal } =
    useCheckout()
  const {
    tipAmount: tip,
    tipEnabled,
    fundraisingAmount: fundraising
  } = usePayment()
  const {
    gratuityServiceCharges,
    processingServiceCharges,
    nonDeliveryNonGratuityNonUbpServiceCharges,
    deliveryChargeTotalAmount,
    tax
  } = useCartLineItems(cart as any)(tip, fundraising)
  const { spiSurchargingEnabled } = useSpi()
  const is3pdDelivery =
    uses3pd && cart?.diningOptionBehavior === DiningOptionBehavior.Delivery
  const freeDelivery = useAppliedFreeDelivery()
  // Free delivery strikes through the Delivery line instead of joining Discounts.
  const discountsRowFundedOffersTotal = useDiscountsRowFundedOffersTotal()
  const shippingItem = getEcommShippingFee(cart)

  const deliveryCharge = deliveryChargeTotalAmount || 0
  const {
    coveredByDeliveryFee,
    uncoveredCredit,
    discountedDeliveryFee,
    isDeliveryFeeDiscounted
  } = splitFreeDeliveryCredit(freeDelivery.amount, deliveryCharge)
  // orderTotal already has the funded offers taken off it, and the uncovered part
  // stays reported in Discounts.
  const preOfferOrderTotal = orderTotal + coveredByDeliveryFee

  const discountsTotal = calculateDiscountsTotal(
    cart?.order?.discounts?.globalReward?.amount || 0,
    discountsRowFundedOffersTotal + uncoveredCredit,
    cart?.order?.discountsTotal
  )

  // For ecommerce orders, use subtotal excluding shipping; otherwise use regular subtotal
  const displaySubtotal =
    channel === Channel.ECOMM
      ? calculateSubtotalExcludingShippingFromCart(cart)
      : subtotal

  return (
    <div className='orderPrices'>
      {!!displaySubtotal && (
        <>
          <ItemPrice
            loading={loadingCart}
            title={t('order-prices.subtotal')}
            amount={displaySubtotal}
          />
          {shippingItem && (
            <ItemPrice
              loading={loadingCart}
              title={t('order-prices.shipping')}
              amount={shippingItem.price}
            />
          )}
          {discountsTotal > 0 && (
            <ItemPrice
              loading={loadingCart}
              title={t('order-prices.discounts')}
              amount={discountsTotal}
              negative={true}
            />
          )}
        </>
      )}
      {!!displaySubtotal && (
        <FeeLineItems
          loading={loadingCart}
          diningOptionBehavior={cart?.diningOptionBehavior}
          deliveryFee={discountedDeliveryFee}
          originalDeliveryFee={
            isDeliveryFeeDiscounted ? deliveryCharge : undefined
          }
          nonDeliveryNonGratuityNonUbpServiceCharges={
            nonDeliveryNonGratuityNonUbpServiceCharges
          }
          gratuityServiceCharges={gratuityServiceCharges}
          processingServiceCharges={processingServiceCharges}
          tax={tax}
        />
      )}
      {tipEnabled && tip > 0 && (
        <ItemPrice
          loading={loadingCart}
          title={
            is3pdDelivery ? t('order-prices.driver-tip') : t('order-prices.tip')
          }
          amount={tip}
        />
      )}
      {fundraising > 0 && (
        <ItemPrice
          loading={loadingCart}
          title={t('order-prices.support-and-give')}
          amount={fundraising}
        />
      )}
      {giftCardAppliedAmount > 0 && (
        <ItemPrice
          loading={loadingCart}
          title={t('order-prices.gift-card')}
          amount={giftCardAppliedAmount}
          negative={true}
        />
      )}
      {toastCashAppliedAmount > 0 && (
        <ItemPrice
          loading={loadingCart}
          title={t('order-prices.toast-cash')}
          amount={toastCashAppliedAmount}
          negative={true}
        />
      )}
      <div className='row total' data-testid='order-total'>
        {loadingCart ? (
          <>
            <Skeleton width={80} />
            <Skeleton width={60} />
          </>
        ) : (
          <>
            <div>{t('order-prices.total')}</div>
            <div>
              {isDeliveryFeeDiscounted ? (
                <DiscountedPrice
                  originalAmount={preOfferOrderTotal}
                  amount={orderTotal}
                  testIdPrefix='order-total'
                />
              ) : (
                <FormattedPrice value={orderTotal} />
              )}
            </div>
          </>
        )}
      </div>
      {canShowPaymentAdjustments && (
        <>
          <CreditCardSurchargeDisclaimerWithItemPrice
            surcharges={surcharges}
            spiSurchargingEnabled={spiSurchargingEnabled}
            loadingCart={loadingCart}
          />
          <CashRoundingDisclaimer />
        </>
      )}
    </div>
  )
}

export default OrderPrices
